/**
 * RUN 97 — putting the Cap Milestones Watch on one sheet of A4, under a filename that carries its
 * date.
 *
 * The Technical Delegate asked for *"a button to generate an updated report (dated on the
 * report)"*. The hand-made editions were one-page PDFs. No PDF writer is added: this is the press
 * RUN 90 (`umpires/matrixPrint.ts`) and RUN 93 (`hearings/hearingPrint.ts`) already established,
 * reused whole, and the argument for each piece is theirs:
 *
 *   * **the `@page` rule is injected for the length of the press** and removed after it, never
 *     written into `editorial.css` — an unnamed `@page` appended there would re-margin every
 *     document this system prints (RUN 90, decision 2);
 *   * **its margin is zero**, so the browser has no margin box to draw its date, URL and
 *     application name into, and the sheet's own padding stands in for the margin (RUN 93, part
 *     1 — the padding lives in the stylesheet's RUN 97 section);
 *   * **the page is emptied by marking the sheet's ancestor chain** and lifting everything off it
 *     out of the layout, so the Command Desk behind the sheet does not follow it as a run of blank
 *     pages (RUN 90, decision 3);
 *   * **it is scaled to ONE sheet with `zoom`**, measured before the dialogue opens, because a
 *     transform leaves the layout box full size and the engine paginates against that (RUN 90,
 *     decision 1). A watch of thirty-odd rows fits at full size; the scale is there for the day a
 *     round of knockouts puts fifty players within reach;
 *   * **`document.title` names the file**, because `Save as PDF` names it after the title and
 *     there is no other lever: `Cap-Milestones-Watch-2026-09-27`. Unlike a hearing letter, this
 *     name CARRIES the date — each edition is a different document, dated on its face at the TD's
 *     request, and two days' watches saved into one folder must not overwrite each other.
 *
 * Nothing here runs on screen, and nothing it touches survives the press: one idempotent teardown
 * puts back the title, the classes, the custom properties and the style element.
 */

import { printDocument } from "../hearings/documentText.js";

/** On `body` for the length of the press; the RUN 97 print rules hang off it. */
export const CAP_SHEET_PRINT_BODY_CLASS = "is-capsheet-print";

/** On the sheet being printed. */
export const CAP_SHEET_PRINT_SHEET_CLASS = "capsheet--printing";

/** On each element between the sheet and `body`. */
export const CAP_SHEET_PRINT_ANCESTOR_CLASS = "capsheet__anc";

/**
 * The whole of A4 portrait, 210mm × 297mm, in CSS px at 96dpi: 794 × 1122. The WHOLE page and not
 * the area inside a margin, because the `@page` margin is zero and the 12mm is the sheet's own
 * padding - which is inside what is measured, and is scaled with it.
 */
export const CAP_SHEET_WIDTH_PX = 794;
export const CAP_SHEET_HEIGHT_PX = 1122;

/** RUN 90's re-flow margin, for its reason. */
const CAP_SHEET_SAFETY = 0.95;

/** Below this the type is too small to read across a table; a real watch never gets near it. */
const CAP_SHEET_FLOOR = 0.4;

/** RUN 90's and RUN 93's fallback, for the engine that never fires `afterprint`. */
const CAP_SHEET_FALLBACK_MS = 1500;

/** `Cap-Milestones-Watch-2026-09-27` — the tournament day the watch was generated on. */
export function capSheetFilename(tournamentDay: string): string {
  const safe = /^\d{4}-\d{2}-\d{2}$/.test(tournamentDay) ? tournamentDay : "undated";
  return `Cap-Milestones-Watch-${safe}`;
}

/** The scale that puts a sheet of `width` × `height` CSS px on one portrait page; never above 1. */
export function capSheetScale(width: number, height: number): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return 1;
  const fit = Math.min(CAP_SHEET_WIDTH_PX / width, CAP_SHEET_HEIGHT_PX / height);
  if (fit >= 1) return 1;
  return Math.max(CAP_SHEET_FLOOR, Math.round(fit * CAP_SHEET_SAFETY * 1000) / 1000);
}

/**
 * Print the sheet: measure it, name the file, dress the document for one portrait page, open the
 * browser's dialogue, and undress it again.
 */
export function printCapSheet(sheet: HTMLElement | null, tournamentDay: string): void {
  if (sheet === null || typeof window === "undefined" || typeof document === "undefined") return;

  const width = sheet.scrollWidth;
  sheet.style.setProperty("--capsheet-scale", String(capSheetScale(width, sheet.scrollHeight)));
  /* Held to the width it measured at, so the paper re-lays the table exactly as it was measured
     (RUN 90's `--matprint-w`, for its reason). */
  if (width > 0) sheet.style.setProperty("--capsheet-w", `${String(width)}px`);
  sheet.classList.add(CAP_SHEET_PRINT_SHEET_CLASS);
  const ancestors: HTMLElement[] = [];
  for (let node = sheet.parentElement; node !== null && node !== document.body; node = node.parentElement) {
    node.classList.add(CAP_SHEET_PRINT_ANCESTOR_CLASS);
    ancestors.push(node);
  }
  document.body.classList.add(CAP_SHEET_PRINT_BODY_CLASS);

  const restoreTitle = document.title;
  document.title = capSheetFilename(tournamentDay);

  const page = document.createElement("style");
  page.dataset.capSheetPrint = "";
  page.textContent = "@page { size: A4 portrait; margin: 0; }";
  document.head.append(page);

  let timer = 0;
  let torn = false;
  const teardown = (): void => {
    if (torn) return;
    torn = true;
    window.removeEventListener("afterprint", teardown);
    window.clearTimeout(timer);
    page.remove();
    document.title = restoreTitle;
    document.body.classList.remove(CAP_SHEET_PRINT_BODY_CLASS);
    for (const node of ancestors) node.classList.remove(CAP_SHEET_PRINT_ANCESTOR_CLASS);
    sheet.classList.remove(CAP_SHEET_PRINT_SHEET_CLASS);
    sheet.style.removeProperty("--capsheet-scale");
    sheet.style.removeProperty("--capsheet-w");
  };

  window.addEventListener("afterprint", teardown);
  printDocument();
  if (!torn) timer = window.setTimeout(teardown, CAP_SHEET_FALLBACK_MS);
}
